import { PropsWithChildren, useCallback } from 'react';
import React, { useContext } from 'react';

import useFeatureToggles from '@/common/hooks/feature-toggles';
import { match } from '@/common/utils/match';
import { useActions, useAppState } from '@/overmind';

import { LeafletMap } from '../LeafletMap/LeafletMap';
import { SplitViewContext } from './SplitViewContext';
import { useHighlightedFeatures } from '@/features/execute-calculation-set/components/Calculation/hooks/useHighlightedFeatures';
import { MapProvider } from '@/features/execute-calculation-set/components/ArcGisMapContainer/MapProvider';
import { MapViewProvider } from '@/features/execute-calculation-set/components/ArcGisMapContainer/MapViewProvider';
import { Map } from '@/features/execute-calculation-set/components/ArcGisMapContainer/Map';
import { EsriFeaturePicker } from '@/features/execute-calculation-set/components/ArcGisMapContainer/EsriFeaturePicker';
import { useExternalNetworkInjectionId } from '@/features/execute-calculation-set/components/Calculation/hooks/useMvTacticResultLayers';
import { VoltageLevel } from '@/features/execute-calculation-set/overmind/calculation';
import { useAddCalculationSetExecutionResultsToMap } from '@/features/execute-calculation-set/components/ArcGisMapContainer/useAddCalculationSetExecutionResultsToMap';

type MapClickEventPayload = {
  features: any[];
  graphics: any[];
  mapInteractionLocation: { x: number; y: number } | null;
}
  const useTransformerId = () => {
    const state = useAppState().calculation.calculationTactic;
    return state.current === 'LOADED' && state.value.status === 'Completed'
      ? state.value.calculationSetResult.grid.functional.powerTransformers[0]
          ?.objectId
      : undefined;
  };

export function SplitViewMap({ children, voltagelevel }: PropsWithChildren<{ voltagelevel: VoltageLevel }>) {
  const [highlightedFeatures] = useHighlightedFeatures();
  const featuresInLayers = useAppState().arcgis.featureGeometries.value;
  const selectedFeature = useAppState().selectedFeature.current
  const { withExtent, onViewInitialized } = useActions().map
  const [{ features, graphics, mapInteractionLocation }, setFeatures] =
    React.useState<MapClickEventPayload>({
      features: [],
      graphics: [],
      mapInteractionLocation: null
    });
  const trigger = useContext(SplitViewContext);
  const isArcGisJsSDKWebMapForBrowsingCalculationResultsEnabled = useFeatureToggles().isArcGisJsSDKWebMapForBrowsingCalculationResultsEnabled();
  const _withExtent = useCallback(withExtent, []);
  const _onViewInitialized = useCallback(onViewInitialized, []);
  const externalNetworkInjectionId = useExternalNetworkInjectionId();
  const transformerId = useTransformerId();
  const EsriMapContainer = React.useMemo(
    () => {
      return (
      <MapProvider>
        <MapViewProvider>
          {(voltagelevel === 'MediumVoltage') ? (
            <Map
              onClick={setFeatures}
              unselectFeature={!Boolean(selectedFeature)}
              withExtent={_withExtent}
              onViewInitialized={_onViewInitialized}
              startingPoint={externalNetworkInjectionId}
              featuresInLayers={featuresInLayers}
              highlightedFeatures={highlightedFeatures}
              useAddCalculationSetExecutionResultsToMap={useAddCalculationSetExecutionResultsToMap}
            />
          ) :
          (
            <Map
              onClick={setFeatures}
              unselectFeature={!Boolean(selectedFeature)}
              withExtent={_withExtent}
              onViewInitialized={_onViewInitialized}
              startingPoint={externalNetworkInjectionId}
              featuresInLayers={featuresInLayers}
              highlightedFeatures={highlightedFeatures}
              useAddCalculationSetExecutionResultsToMap={useAddCalculationSetExecutionResultsToMap}
            />
          )}
        </MapViewProvider>
      </MapProvider>
    )},
    [_withExtent, featuresInLayers, highlightedFeatures]
  );
  const mapType = selectMapType({
    shouldUseEsriMap: isArcGisJsSDKWebMapForBrowsingCalculationResultsEnabled
  });
  return match.allOf(mapType, {
    ESRI: () => (
      <>
        {EsriMapContainer}
        <EsriFeaturePicker
          features={features}
          graphics={graphics}
          mapInteractionLocation={mapInteractionLocation}
        />
      </>
    ),
    LEAFLET: () => (
      <LeafletMap invalidateMapTrigger={trigger}>{children}</LeafletMap>
    )
  });
}
export function selectMapType({
  shouldUseEsriMap
}: {
  shouldUseEsriMap: boolean;
}): 'LEAFLET' | 'ESRI' {
  if (shouldUseEsriMap) {
    return 'ESRI';
  }

  return 'LEAFLET';
}
